import { PriceTemplateResolvedLifecycle } from './price-template.model';

/**
 * Nota de vigencia que acompaña la fecha de vencimiento en el listado y el detalle:
 * "en 5 días", "vence hoy", "hace 2 días", "convertida el 20/08".
 *
 * Es texto de presentación, así que vive en el front y no en el API — el estado en sí
 * (`resolvedLifecycle`) sí lo calcula el backend, que es la fuente de verdad.
 */
export interface VigenciaNote {
    text: string;
    /** 'warn' cuando urge actuar (vence hoy o ya venció); 'muted' el resto. */
    tone: 'warn' | 'muted';
}

const MS_PER_DAY = 86400000;

function startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function daysBetween(from: Date, to: Date): number {
    return Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / MS_PER_DAY);
}

/** Texto para una cotización sin fecha de vencimiento (FINA-3272). */
export const NO_EXPIRY_LABEL = 'Sin vencimiento';

export function vigenciaNote(lifecycle: PriceTemplateResolvedLifecycle | undefined, expiresAt: string | null | undefined, convertedAt?: string, today: Date = new Date()): VigenciaNote {
    if (lifecycle === 'convertida') {
        const when = convertedAt ? new Date(convertedAt) : null;
        const label = when ? `convertida el ${String(when.getDate()).padStart(2, '0')}/${String(when.getMonth() + 1).padStart(2, '0')}` : 'convertida';
        return { text: label, tone: 'muted' };
    }

    // Sin fecha: no vence. La celda de fecha muestra NO_EXPIRY_LABEL, así que la nota va vacía.
    if (!expiresAt) return { text: '', tone: 'muted' };

    const diff = daysBetween(today, new Date(expiresAt));

    if (diff < 0) {
        const days = Math.abs(diff);
        return { text: `hace ${days} día${days === 1 ? '' : 's'}`, tone: 'warn' };
    }
    if (diff === 0) return { text: 'vence hoy', tone: 'warn' };

    return { text: `en ${diff} día${diff === 1 ? '' : 's'}`, tone: 'muted' };
}

/** Iniciales para los avatares de cliente y de vendedor. */
export function initials(name: string | undefined): string {
    if (!name) return '?';
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(word => word[0].toUpperCase())
        .join('');
}
